import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Map, Plus, Trash2, Upload, ExternalLink, Train, ParkingCircle, Bike, Utensils, ShoppingCart, Landmark, Bus, Plane, Phone, Lightbulb, Save } from "lucide-react";

const LISTS = [
  { key: "parking", label: "Parcheggi", icon: ParkingCircle, cols: [["name", "Nome"], ["address", "Indirizzo"], ["distance", "Distanza"], ["cost", "Costo"], ["maps_link", "Link Maps"], ["notes", "Note"]], bools: [["covered", "Coperto"], ["h24", "H24"]] },
  { key: "bike", label: "Bike / Monopattini", icon: Bike, cols: [["provider", "Provider (BikeMi/Lime/Dott/RideMovi)"], ["nearest", "Punto più vicino"], ["link", "Link app/mappa"], ["notes", "Note"]] },
  { key: "taxi_ncc", label: "Taxi / NCC / Transfer", icon: Bus, cols: [["name", "Nome"], ["phone", "Telefono"], ["whatsapp", "WhatsApp"], ["link", "Link"], ["notes", "Note"]] },
  { key: "restaurants", label: "Ristoranti consigliati", icon: Utensils, cols: [["name", "Nome"], ["category", "Tipo (colazione/pizza/aperitivo/tipico)"], ["distance", "Distanza"], ["price", "Fascia prezzo"], ["maps_link", "Link Maps"], ["note", "Note"]] },
  { key: "supermarkets", label: "Supermercati", icon: ShoppingCart, cols: [["name", "Nome"], ["address", "Indirizzo"], ["distance", "Distanza"], ["hours", "Orari"], ["maps_link", "Link Maps"]] },
  { key: "attractions", label: "Attrazioni / Punti di interesse", icon: Landmark, cols: [["name", "Nome"], ["distance", "Distanza"], ["maps_link", "Link Maps"], ["note", "Note"]] },
  { key: "airports", label: "Aeroporti", icon: Plane, cols: [["name", "Aeroporto"], ["how_to", "Come arrivare"], ["time", "Tempo"], ["cost", "Costo indicativo"], ["maps_link", "Link Maps"]], global: true },
  { key: "emergency", label: "Emergenze", icon: Phone, cols: [["label", "Servizio"], ["phone", "Numero"], ["note", "Note"]], global: true },
  { key: "host_tips", label: "Consigli dell'Host", icon: Lightbulb, cols: [["title", "Titolo"], ["text", "Testo"]] },
];

export default function LocalGuide() {
  const [apts, setApts] = useState([]);
  const [scope, setScope] = useState("global");
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/apartments/lookup").then((r) => setApts(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const p = scope === "global" ? "" : `?apartment_id=${scope}`;
    api.get(`/local-guide${p}`).then((r) => setDoc(r.data)).catch(() => {});
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const setList = (key, arr) => setDoc((d) => ({ ...d, [key]: arr }));
  const addRow = (key, cols, bools) => setDoc((d) => ({ ...d, [key]: [...(d[key] || []), { ...Object.fromEntries(cols.map((c) => [c[0], ""])), ...Object.fromEntries((bools || []).map((b) => [b[0], false])) }] }));
  const setObj = (key, field, val) => setDoc((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: val } }));

  const save = async () => {
    try {
      setSaving(true);
      const payload = { scope, atm_map_note: doc.atm_map_note, transport: doc.transport, tourist_bus: doc.tourist_bus };
      LISTS.forEach((l) => { payload[l.key] = doc[l.key] || []; });
      const r = await api.put("/local-guide", payload);
      setDoc(r.data);
      toast.success("Guida locale salvata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const uploadAtm = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/local-guide/atm-map", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc((d) => ({ ...d, atm_map_url: r.data.atm_map_url }));
      toast.success("Mappa ATM caricata");
    } catch (er) { toast.error(er?.response?.data?.detail || "Errore upload"); }
  };

  if (!doc) return <p className="py-16 text-center text-sm text-slate-400">Caricamento…</p>;
  const isGlobal = scope === "global";

  return (
    <div className="space-y-6 pb-24" data-testid="local-guide">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-600"><Map size={16} /> Urban Local Guide / Mappe</div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Contenuti <b>globali</b> (default per tutti) con <b>override per appartamento</b>. La pagina ospite usa prima i dati dell'appartamento, poi quelli globali.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-64" data-testid="lg-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="global">🌍 Globale (default)</SelectItem>
              {apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code ? `${a.code} · ` : ""}{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="gap-2" onClick={save} disabled={saving} data-testid="lg-save"><Save size={15} /> Salva</Button>
        </div>
      </div>

      {/* ATM map (global only) */}
      {isGlobal && (
        <Card className="border-slate-200 p-5" data-testid="lg-atm">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Train size={16} /> Mappa ATM Milano (globale)</div>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={uploadAtm} data-testid="lg-atm-input" />
            <Button variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} data-testid="lg-atm-upload"><Upload size={15} /> Carica / aggiorna mappa</Button>
            {doc.atm_map_url && <a href={doc.atm_map_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-indigo-600"><ExternalLink size={14} /> Mappa attuale</a>}
          </div>
          <Input className="mt-3" value={doc.atm_map_note || ""} onChange={(e) => setDoc((d) => ({ ...d, atm_map_note: e.target.value }))} placeholder="Nota mappa ATM" />
        </Card>
      )}

      {/* Transport (per apartment recommended) */}
      <Card className="border-slate-200 p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Train size={16} /> Trasporti pubblici vicini {isGlobal ? "(default)" : ""}</div>
        <div className="grid gap-3 sm:grid-cols-3">
          {[["metro", "Metro più vicina"], ["tram", "Tram più vicino"], ["bus", "Bus più vicino"], ["walk_distance", "Distanza a piedi"], ["walk_time", "Tempo stimato"], ["maps_link", "Link Google Maps"]].map(([f, l]) => (
            <Input key={f} value={doc.transport?.[f] || ""} placeholder={l} onChange={(e) => setObj("transport", f, e.target.value)} data-testid={`lg-transport-${f}`} />
          ))}
        </div>
      </Card>

      {/* Tourist bus */}
      <Card className="border-slate-200 p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Bus size={16} /> Bus turistico</div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[["operator", "Operatore (City Sightseeing…)"], ["stop", "Fermata più vicina"], ["tickets_link", "Link biglietti"], ["maps_link", "Link Google Maps"]].map(([f, l]) => (
            <Input key={f} value={doc.tourist_bus?.[f] || ""} placeholder={l} onChange={(e) => setObj("tourist_bus", f, e.target.value)} />
          ))}
        </div>
      </Card>

      {/* Repeater lists */}
      {LISTS.map((L) => (
        <Card key={L.key} className="border-slate-200 p-5" data-testid={`lg-section-${L.key}`}>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><L.icon size={16} /> {L.label} {L.global && <span className="text-xs font-normal text-slate-400">(consigliato globale)</span>}</div>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => addRow(L.key, L.cols, L.bools)} data-testid={`lg-add-${L.key}`}><Plus size={14} /> Aggiungi</Button>
          </div>
          <div className="space-y-3">
            {(doc[L.key] || []).length === 0 && <p className="text-xs text-slate-400">Nessuna voce.</p>}
            {(doc[L.key] || []).map((row, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 p-2">
                {L.cols.map(([f, ph]) => (
                  <Input key={f} value={row[f] || ""} placeholder={ph} className="h-9 min-w-[140px] flex-1"
                    onChange={(e) => { const a = [...doc[L.key]]; a[i] = { ...a[i], [f]: e.target.value }; setList(L.key, a); }} />
                ))}
                {(L.bools || []).map(([f, lb]) => (
                  <label key={f} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Switch checked={!!row[f]} onCheckedChange={(v) => { const a = [...doc[L.key]]; a[i] = { ...a[i], [f]: v }; setList(L.key, a); }} /> {lb}
                  </label>
                ))}
                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setList(L.key, doc[L.key].filter((_, idx) => idx !== i))}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button className="gap-2" onClick={save} disabled={saving} data-testid="lg-save-bottom"><Save size={15} /> Salva guida locale</Button>
      </div>
    </div>
  );
}
